<template>
  <!-- Reusable pulsar-themed loading indicator. Drop in anywhere a
       section / list / dashboard panel is fetching data. The visual
       metaphor: a rotating neutron star whose lighthouse beams sweep
       around a glowing core, with EM "pulses" emanating outward.
       Pure SVG + CSS animations — no JS, no canvas, no lottie. -->
  <div
    role="status"
    aria-live="polite"
    :class="['flex flex-col items-center justify-center gap-3', containerClass]"
  >
    <div :class="['relative shrink-0', sizeClass]">
      <!-- Outermost ring — slow counter-rotation to add visual depth.
           A dashed stroke + linear gradient stops keeps it from
           reading as a generic "loading wheel"; gradient swirls with
           the rotation. -->
      <svg
        class="absolute inset-0 w-full h-full pulsar-ring"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <defs>
          <linearGradient :id="gradId('ring')" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="rgb(168, 85, 247)" stop-opacity="0.9" />
            <stop offset="50%" stop-color="rgb(99, 102, 241)" stop-opacity="0.7" />
            <stop offset="100%" stop-color="rgb(59, 130, 246)" stop-opacity="0.4" />
          </linearGradient>
          <linearGradient :id="gradId('beam')" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%"  stop-color="rgba(168, 85, 247, 0.9)" />
            <stop offset="40%" stop-color="rgba(168, 85, 247, 0.45)" />
            <stop offset="100%" stop-color="rgba(168, 85, 247, 0)" />
          </linearGradient>
        </defs>
        <circle
          cx="50" cy="50" r="46"
          :stroke="`url(#${gradId('ring')})`"
          stroke-width="1.5"
          fill="none"
          stroke-dasharray="6 12"
          stroke-linecap="round"
        />
      </svg>

      <!-- Lighthouse beams — two diametrically opposite triangular
           shafts that rotate around the core. Wrapped in a single <g>
           so we only animate one element. -->
      <svg
        class="absolute inset-0 w-full h-full pulsar-beams"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <g transform-origin="50 50">
          <!-- Top beam — narrow at the core, fans out at the rim. -->
          <path
            d="M 50 50 L 44 4 L 56 4 Z"
            :fill="`url(#${gradId('beam')})`"
          />
          <!-- Bottom beam (180° opposite). -->
          <path
            d="M 50 50 L 44 96 L 56 96 Z"
            :fill="`url(#${gradId('beam')})`"
            transform="rotate(180 50 50)"
          />
        </g>
      </svg>

      <!-- Emanating ripples. Three concentric rings spawn at the
           core and grow outward, fading as they expand. Staggered
           with animation-delay so they don't bunch up. -->
      <span
        v-for="i in 3" :key="i"
        :style="{ animationDelay: `${(i - 1) * 0.6}s` }"
        class="absolute inset-0 rounded-full border border-purple-400/60 dark:border-purple-300/60 pulsar-ripple"
        aria-hidden="true"
      ></span>

      <!-- Glowing core. Sits dead center; pulses scale + shadow on
           a tight 1.2s loop so the indicator feels alive even at
           small sizes. -->
      <div class="absolute inset-0 flex items-center justify-center">
        <span class="block w-1/4 h-1/4 rounded-full bg-purple-500 dark:bg-purple-400 pulsar-core"></span>
      </div>
    </div>

    <p
      v-if="resolvedLabel"
      class="text-sm text-gray-500 dark:text-gray-400 font-medium"
    >
      {{ resolvedLabel }}
    </p>
  </div>
</template>

<script setup lang="ts">
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 🌀 PulsarLoader — branded loading indicator.
///
/// Use anywhere a section is fetching data. The visual signature
/// (rotating beams + pulsing core + emanating ripples) reads as
/// "Pulsar is working" rather than the generic spinner the browser
/// already provides. Reuses across query-history, anomaly drawer,
/// dashboard panels, etc.
///
/// Three sizes:
///   sm — 32px, fits inside a button or inline next to text
///   md — 64px, default for empty-table loading states
///   lg — 96px, for full-section overlays
///
/// Pass `label` to show text underneath (defaults to "Loading…"). Pass
/// `label=""` (empty string) to render the indicator alone for tight
/// spaces — accessibility falls back to role="status" + aria-live so
/// screen readers still announce the loading state.
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const props = withDefaults(defineProps<{
  size?: 'sm' | 'md' | 'lg'
  /** Text shown beneath the indicator. `undefined` = default "Loading…",
   *  empty string = no text. */
  label?: string
  /** Extra classes for the outer wrapper (e.g. min-height for a
   *  centered placeholder card). */
  containerClass?: string
}>(), {
  size: 'md',
  label: undefined,
  containerClass: '',
})

// Generate a per-instance gradient ID so multiple loaders on the
// same page don't fight over a global `url(#...)` reference. SVG
// `<defs>` IDs are document-scoped — without this, the second loader
// would inherit the first's gradient orientation/colors silently.
const uid = `pl${Math.random().toString(36).slice(2, 8)}`
function gradId(suffix: string) { return `${uid}-${suffix}` }

const sizeClass = computed(() => ({
  sm: 'w-8 h-8',
  md: 'w-16 h-16',
  lg: 'w-24 h-24',
}[props.size]))

const resolvedLabel = computed(() =>
  props.label === undefined ? 'Loading…' : props.label,
)
</script>

<style scoped>
/* Animations live in <style scoped> so they don't conflict with the
   global stylesheet, but the keyframe names are uniquely-prefixed so
   <style scoped> doesn't isolate them away from the SVG rules above
   (Vue's scoped styles use attribute selectors which carry through
   to children).

   Timing rationale:
     - beams: 2.4s linear — a slow sweep feels deliberate, not anxious
     - ring:  10s reverse — barely-perceptible counter-motion adds depth
     - core:  1.2s ease    — fast enough to read as "alive" at small sizes
     - ripples: 2.4s, staggered — pulse out roughly once per beam sweep */

.pulsar-beams {
  animation: pulsar-spin 2.4s linear infinite;
  transform-origin: center;
}
.pulsar-ring {
  animation: pulsar-spin-rev 10s linear infinite;
  transform-origin: center;
  opacity: 0.7;
}
.pulsar-core {
  animation: pulsar-pulse 1.2s ease-in-out infinite;
  box-shadow:
    0 0 12px rgba(168, 85, 247, 0.7),
    0 0 24px rgba(168, 85, 247, 0.35);
}
.pulsar-ripple {
  animation: pulsar-ripple 2.4s ease-out infinite;
  transform: scale(0.4);
  opacity: 0;
}

@keyframes pulsar-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes pulsar-spin-rev {
  from { transform: rotate(0deg); }
  to   { transform: rotate(-360deg); }
}
@keyframes pulsar-pulse {
  0%, 100% { transform: scale(1);   opacity: 1;   }
  50%      { transform: scale(1.4); opacity: 0.6; }
}
@keyframes pulsar-ripple {
  0%   { transform: scale(0.4); opacity: 0.85; }
  80%  { opacity: 0.05; }
  100% { transform: scale(2);   opacity: 0;    }
}

/* Honor the user's reduced-motion preference. We still convey
   "loading" via the ring's static gradient + the label, but stop
   the kinetic animations entirely. */
@media (prefers-reduced-motion: reduce) {
  .pulsar-beams,
  .pulsar-ring,
  .pulsar-core,
  .pulsar-ripple {
    animation: none;
  }
  .pulsar-core {
    opacity: 0.85;
  }
}
</style>
